import { SCALE, type LmsrQuote, type LmsrSide } from "./lmsrMath";

const BPS_DENOMINATOR = 10_000;
const AFFORDABILITY_EPSILON = 0.000001;
export const DEFAULT_LMSR_B = 500 * SCALE;

const toTokenAmount = (microAmount: number): number => microAmount / SCALE;

export interface BinaryMarketSellEstimate {
  gross_refund: number;
  fee: number;
  payout: number;
  new_odds_after_sale: {
    yes: number;
    no: number;
  };
}

export interface MultiOutcomeState {
  outcome_id: number;
  q?: number;
  q_yes: number;
  q_no: number;
  volume: number;
}

export interface MultiOutcomeRowPrice {
  yesPrice: number;
  noPrice: number;
}

export const resolveLmsrB = (b?: number | null): number =>
  b && b > 0 ? b : DEFAULT_LMSR_B;

export const buildMultiOutcomeStates = (
  outcomeIds: number[],
  outcomeStates?: MultiOutcomeState[],
): MultiOutcomeState[] => {
  const stateByOutcomeId = new Map(
    (outcomeStates ?? []).map((state) => [state.outcome_id, state]),
  );

  return outcomeIds.map((outcomeId) => {
    const existingState = stateByOutcomeId.get(outcomeId);
    return {
      outcome_id: outcomeId,
      q: existingState?.q ?? existingState?.q_yes ?? 0,
      q_yes: existingState?.q_yes ?? existingState?.q ?? 0,
      q_no: existingState?.q_no ?? 0,
      volume: existingState?.volume ?? 0,
    };
  });
};

// Mirrors the binary LMSR cost math used on-chain for version 2 markets.
const lmsrCost = (qYes: number, qNo: number, b: number): number => {
  if (b <= 0) {
    return 0;
  }

  const bFloat = b;
  const yesRatio = qYes / bFloat;
  const noRatio = qNo / bFloat;
  const maxRatio = Math.max(yesRatio, noRatio);

  return (
    bFloat *
    (maxRatio +
      Math.log(Math.exp(yesRatio - maxRatio) + Math.exp(noRatio - maxRatio)))
  );
};

const lmsrBuyCost = (
  qYes: number,
  qNo: number,
  b: number,
  outcomeIsYes: boolean,
  deltaQ: number,
): number => {
  const before = lmsrCost(qYes, qNo, b);
  const after = outcomeIsYes
    ? lmsrCost(qYes + deltaQ, qNo, b)
    : lmsrCost(qYes, qNo + deltaQ, b);

  return after - before;
};

const lmsrTotalCharge = (
  qYes: number,
  qNo: number,
  b: number,
  outcomeIsYes: boolean,
  deltaQ: number,
  feeBps: number,
): number => {
  const rawCost = lmsrBuyCost(qYes, qNo, b, outcomeIsYes, deltaQ);
  return rawCost * (1 + feeBps / BPS_DENOMINATOR);
};

export const computeBinarySharesFromBudget = (
  qYes: number,
  qNo: number,
  b: number,
  outcomeIsYes: boolean,
  budget: number,
  feeBps: number,
): number => {
  if (budget <= 0 || b <= 0) {
    return 0;
  }

  const budgetFloat = budget;
  let low = 0;
  let high = 1;

  while (
    lmsrTotalCharge(qYes, qNo, b, outcomeIsYes, high, feeBps) <=
    budgetFloat + AFFORDABILITY_EPSILON
  ) {
    low = high;

    const nextHigh = high * 2;
    if (nextHigh <= high) {
      return high;
    }
    high = nextHigh;
  }

  while (low + 1 < high) {
    const mid = low + Math.trunc((high - low) / 2);

    if (
      lmsrTotalCharge(qYes, qNo, b, outcomeIsYes, mid, feeBps) <=
      budgetFloat + AFFORDABILITY_EPSILON
    ) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return low;
};

const computeBinaryProbabilityYes = (
  qYes: number,
  qNo: number,
  b: number,
): number => {
  if (b <= 0) {
    return 0.5;
  }

  const yesRatio = qYes / b;
  const noRatio = qNo / b;
  const maxRatio = Math.max(yesRatio, noRatio);
  const expYes = Math.exp(yesRatio - maxRatio);
  const expNo = Math.exp(noRatio - maxRatio);

  return expYes / (expYes + expNo);
};

const stableLogSumExp = (values: number[]): number => {
  const maxValue = Math.max(...values);
  const scaledTotal = values.reduce(
    (sum, value) => sum + Math.exp(value - maxValue),
    0,
  );

  return maxValue + Math.log(scaledTotal);
};

export const categoricalLmsrPrices = (
  qValues: number[],
  b: number,
): number[] => {
  if (qValues.length === 0 || b <= 0) {
    return [];
  }

  const scaledValues = qValues.map((value) => value / b);
  const logDenominator = stableLogSumExp(scaledValues);

  return scaledValues.map((value) => Math.exp(value - logDenominator));
};

export const getMultiOutcomeRowPrices = (
  outcomeStates: MultiOutcomeState[],
  b: number,
): Record<number, MultiOutcomeRowPrice> => {
  if (outcomeStates.length === 0) {
    return {};
  }

  const effectiveB = resolveLmsrB(b);

  const probabilities = categoricalLmsrPrices(
    outcomeStates.map((state) => state.q ?? state.q_yes ?? 0),
    effectiveB,
  );

  return Object.fromEntries(
    outcomeStates.map((state, index) => [
      state.outcome_id,
      {
        yesPrice: probabilities[index],
        noPrice: 1 - probabilities[index],
      },
    ]),
  );
};

const computeBinaryRefundForShares = (
  qYes: number,
  qNo: number,
  b: number,
  outcomeIsYes: boolean,
  deltaQ: number,
): number => {
  if (deltaQ <= 0 || b <= 0) {
    return 0;
  }

  const before = lmsrCost(qYes, qNo, b);
  const after = outcomeIsYes
    ? lmsrCost(qYes - deltaQ, qNo, b)
    : lmsrCost(qYes, qNo - deltaQ, b);
  const refund = before - after;

  if (refund <= 0) {
    return 0;
  }

  return Math.floor(refund);
};

export const estimateBinaryMarketBuyQuote = (
  qYes: number,
  qNo: number,
  b: number,
  side: LmsrSide,
  spendAmount: number,
  feeBps: number,
): LmsrQuote | null => {
  if (spendAmount <= 0 || b <= 0) {
    return null;
  }

  const outcomeIsYes = side === "yes";
  const grossSpendMicro = Math.trunc(spendAmount * SCALE);
  const feeAmountMicro = Math.trunc((grossSpendMicro * feeBps) / BPS_DENOMINATOR);
  const budgetWithoutFeeMicro = grossSpendMicro - feeAmountMicro;
  const deltaQ = computeBinarySharesFromBudget(
    qYes,
    qNo,
    b,
    outcomeIsYes,
    budgetWithoutFeeMicro,
    0,
  );

  const nextQYes = outcomeIsYes ? qYes + deltaQ : qYes;
  const nextQNo = outcomeIsYes ? qNo : qNo + deltaQ;
  const currentProbYes = computeBinaryProbabilityYes(qYes, qNo, b);
  const nextProbYes = computeBinaryProbabilityYes(nextQYes, nextQNo, b);

  // The contract spends the full transfer amount: fee is carved out first and
  // the remaining net budget is applied to the LMSR buy. Expose that same
  // spend breakdown so the UI matches the on-chain execution path.
  return {
    estimated_shares: toTokenAmount(deltaQ),
    raw_cost: toTokenAmount(budgetWithoutFeeMicro),
    fee: toTokenAmount(feeAmountMicro),
    total_charge: toTokenAmount(grossSpendMicro),
    refund: 0,
    avg_price_per_share:
      deltaQ > 0 ? grossSpendMicro / deltaQ : 0,
    min_shares_for_slippage: Math.trunc((deltaQ * 0.98) / SCALE),
    current_odds: {
      yes: currentProbYes * 100,
      no: (1 - currentProbYes) * 100,
    },
    new_odds_after_purchase: {
      yes: nextProbYes * 100,
      no: (1 - nextProbYes) * 100,
    },
    exceeds_liquidity_cap: false,
  };
};

export const estimateBinaryMarketSellQuote = (
  qYes: number,
  qNo: number,
  b: number,
  side: LmsrSide,
  sharesAmount: number,
  feeBps: number,
): BinaryMarketSellEstimate | null => {
  if (sharesAmount <= 0 || b <= 0) {
    return null;
  }

  const outcomeIsYes = side === "yes";
  // Mirror the exact fixed-point quantity the frontend submits in the sell action.
  const sharesMicro = Math.round(sharesAmount * SCALE);
  if (sharesMicro <= 0) {
    return null;
  }

  const grossRefundMicro = computeBinaryRefundForShares(
    qYes,
    qNo,
    b,
    outcomeIsYes,
    sharesMicro,
  );
  const sellFeeMicro = Math.trunc(
    (grossRefundMicro * feeBps) / BPS_DENOMINATOR,
  );
  const netPayoutMicro = grossRefundMicro - sellFeeMicro;
  const nextQYes = outcomeIsYes ? Math.max(0, qYes - sharesMicro) : qYes;
  const nextQNo = outcomeIsYes ? qNo : Math.max(0, qNo - sharesMicro);
  const nextProbYes = computeBinaryProbabilityYes(nextQYes, nextQNo, b);

  return {
    gross_refund: toTokenAmount(grossRefundMicro),
    fee: toTokenAmount(sellFeeMicro),
    payout: toTokenAmount(netPayoutMicro),
    new_odds_after_sale: {
      yes: nextProbYes * 100,
      no: (1 - nextProbYes) * 100,
    },
  };
};
